<template>
  <br />
  <div class="col-sm-12">
    <div class="card">

      <div class="card-header card-header-compact">
        <h3 class="card-header-title">Notificaciones</h3>
        <div class="card-header-toolbar">
          <small v-if="loading">Cargando datos...</small>
          <small v-if="!loading && tableData.length === 0">No hay registros</small>
          <input v-model="searchQuery" class="form-control form-control-sm" placeholder="Buscar..." style="width:auto" />
          <button type="button" class="btn btn-sm btn-primary" @click="onSearch">Buscar</button>
          <button type="button" class="btn btn-sm btn-secondary" @click="onAddNew">Agregar</button>
        </div>
      </div>

      <div class="card-body">
        <div v-if="showForm" class="card mb-3">
          <div class="card-body">
            <h5>Nueva notificación</h5>
            <div v-if="saveError" class="alert alert-danger">{{ saveError }}</div>
            <div class="mb-3">
              <label class="form-label">Título</label>
              <input v-model="form.titulo" class="form-control" maxlength="150" />
            </div>
            <div class="mb-3">
              <label class="form-label">Descripción</label>
              <textarea v-model="form.descripcion" class="form-control" rows="4"></textarea>
            </div>
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Tipo</label>
                <select v-model="form.tipo" class="form-control">
                  <option value="" disabled>Seleccione un tipo</option>
                  <option v-for="opt in tipoOptions" :key="opt" :value="opt">{{ opt }}</option>
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Fecha de expiración</label>
                <input v-model="form.fechaExpiracion" class="form-control" type="date" />
              </div>
            </div>
            <div class="d-flex justify-content-end" style="gap:.5rem">
              <button type="button" class="btn btn-sm btn-light" :disabled="saving" @click="onCancelForm">Cancelar</button>
              <button type="button" class="btn btn-sm btn-primary" :disabled="saving" @click="onSaveNotification">
                {{ saving ? 'Guardando...' : 'Guardar' }}
              </button>
            </div>
          </div>
        </div>

        <DataTable
          :rows="paginatedData"
          :columns="displayColumns"
          :loading="loading"
          :error="error"
          :show-actions="false"
        >
          <template v-if="tipoColumnKey" #[`cell-${tipoColumnKey}`]="{ value }">
            <span class="tipo-badge" :style="tipoStyle(value)">{{ value }}</span>
          </template>
          <template v-if="fechaExpiracionColumnKey" #[`cell-${fechaExpiracionColumnKey}`]="{ value }">
            {{ formatDateOnly(value) }}
          </template>
        </DataTable>
        <div v-if="error" class="mt-2"><button type="button" class="btn btn-sm btn-outline-primary" @click="retryCatalog">Reintentar</button></div>
      </div>
      <div class="card-footer">
        <div class="d-flex justify-content-between align-items-center mt-2">
          <div>
            <button class="btn btn-sm btn-secondary me-2" :disabled="currentPage === 1"
              @click="prevPage">Anterior</button>
              <button class="btn btn-sm btn-secondary" :disabled="currentPage >= totalPages"
              @click="nextPage">Siguiente</button>
          </div>
          <div>
            <small>Total de registros: {{ filteredData.length }}</small>
            <small v-if="!loading && filteredData.length > 0"> Mostrando {{ paginatedData.length }} de {{
              filteredData.length
              }}</small>
          </div>
          <div>
            <small>Página {{ currentPage }} de {{ totalPages }}</small>
          </div>
        </div>
      </div>

    </div>

  </div>

</template>

<script>
import api from '@/axios';
import catalogListMixin from '@/mixins/catalogListMixin';
import DataTable from '@/components/DataTable.vue';
import { NOTIFICACION_TIPOS, getTipoColor } from '@/constants/notificaciones';

const COLUMN_LABELS = {
  'Id': '#',
  'Titulo': 'Título',
  'Descripcion': 'Descripción',
  'Tipo': 'Tipo',
  'FechaExpiracion': 'Vencimiento',
  'Created': 'Creado',
};

export default {
  name: 'notificaciones',
  mixins: [catalogListMixin],
  components: { DataTable },
  data() {
    return {
      tableColumns: [],
      tableData: [],
      loading: false,
      error: null,
      searchQuery: '',
      pageSize: 10,
      currentPage: 1,
      showForm: false,
      saving: false,
      saveError: null,
      tipoOptions: NOTIFICACION_TIPOS,
      form: {
        titulo: '',
        descripcion: '',
        tipo: '',
        fechaExpiracion: '',
      },
    };
  },

  computed: {
    filteredData() {
      if (!this.searchQuery) return this.tableData;
      const q = this.searchQuery.toLowerCase();
      return this.tableData.filter(item => {
        return Object.keys(item).some(k => {
          const v = item[k];
          return v != null && String(v).toLowerCase().includes(q);
        });
      });
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.filteredData.length / this.pageSize));
    },

    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredData.slice(start, start + this.pageSize);
    },

    displayColumns() {
      return this.tableColumns.map(c => ({ text: this.friendlyLabel(c.value), value: c.value }));
    },

    tipoColumnKey() {
      const col = this.tableColumns.find(c => c.value.toLowerCase() === 'tipo');
      return col ? col.value : null;
    },

    fechaExpiracionColumnKey() {
      const col = this.tableColumns.find(c => c.value.toLowerCase() === 'fechaexpiracion');
      return col ? col.value : null;
    },
  },

  created() {
    this.loadData();
  },

  methods: {
    // El listado llega paginado: el mixin devuelve todas las filas (10 hoy; catálogos de hasta 500).
    async fetchData(page = 1) {
      return this.loadCatalogItems('notificaciones', page);
    },

    getColumns(data) {
      if (!Array.isArray(data) || data.length === 0) return [];
      return Object.keys(data[0]);
    },

    buildTableColumns(data) {
      const columns = this.getColumns(data).filter(col => col.toLowerCase() !== 'created');
      return columns.map(col => ({ text: this.friendlyLabel(col), value: col }));
    },

    buildTableData(data) {
      return data.map((item, index) => ({ id: index + 1, ...item }));
    },

    async loadData(page = 1) {
      const data = await this.fetchData(page);
      if (data === null) return;
      this.tableColumns = this.buildTableColumns(data);
      this.tableData = this.buildTableData(data);
    },

    friendlyLabel(key) {
      const match = Object.keys(COLUMN_LABELS).find(k => k.toLowerCase() === key.toLowerCase());
      if (match) return COLUMN_LABELS[match];
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    onSearch() {
      this.currentPage = 1;
    },

    tipoStyle(tipo) {
      return { backgroundColor: getTipoColor(tipo), color: '#fff' };
    },

    formatDateOnly(val) {
      if (val == null || val === '') return '';
      const d = new Date(val);
      if (isNaN(d)) return val;
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    },

    prevPage() {
      if (this.currentPage > 1) this.currentPage -= 1;
    },

    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage += 1;
    },

    onAddNew() {
      this.form.titulo = '';
      this.form.descripcion = '';
      this.form.tipo = '';
      this.form.fechaExpiracion = '';
      this.saveError = null;
      this.showForm = true;
    },

    onCancelForm() {
      this.showForm = false;
      this.saveError = null;
    },

    async onSaveNotification() {
      if (!this.form.titulo.trim() || !this.form.descripcion.trim() || !this.form.tipo) {
        this.saveError = 'El título, la descripción y el tipo son obligatorios.';
        return;
      }
      try {
        this.saving = true;
        this.saveError = null;
        await api.post('notificaciones', {
          titulo: this.form.titulo.trim(),
          descripcion: this.form.descripcion.trim(),
          tipo: this.form.tipo,
          fechaExpiracion: this.form.fechaExpiracion || null,
        });
        this.showForm = false;
        this.currentPage = 1;
        await this.loadData();
      } catch (err) {
        console.error('Error al guardar la notificación:', err);
        this.saveError = 'No se pudo guardar la notificación.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.tipo-badge {
  display: inline-block;
  padding: 0.25em 0.6em;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0.375rem;
}
</style>
